import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Minimize2, Maximize2 } from 'lucide-react';
import { useSocket } from '../contexts/SocketContext';
import { useAppearance } from '../contexts/AppearanceContext';
import MessageRenderer from './MessageRenderer';

const ChatWindow = ({ chat, onClose, onSendMessage, position, onNewMessage, currentUser }) => {
  const [message, setMessage] = useState('');
  const [minimized, setMinimized] = useState(false);
  const messagesEndRef = useRef(null);
  const { socket, sendMessage, markMessagesAsRead } = useSocket();
  const { isSuppleMode, isSuppleLight, isSuppleDark, appearanceMode } = useAppearance();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chat?.messages]);

  useEffect(() => {
    if (socket && chat?.user) {
      // Mark messages as read when opening chat
      markMessagesAsRead(chat.user.id);

      // Listen for new messages
      const handleNewMessage = (message) => {
        if (message.senderId === chat.user.id) {
          onNewMessage(chat.id, {
            text: message.content,
            sender: 'them',
            timestamp: message.createdAt,
          });
        }
      };

      socket.on('new_message', handleNewMessage);

      return () => {
        socket.off('new_message', handleNewMessage);
      };
    }
  }, [socket, chat?.user, chat?.id, onNewMessage, markMessagesAsRead]);

  const handleSend = () => {
    if (message.trim() && chat?.user) {
      // Send via Socket.IO
      sendMessage(chat.user.id, message);
      
      // Update local state immediately
      onSendMessage(chat.id, message);
      setMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleLoadSharedChat = (conversationId) => {
    // Navigate to the shared conversation
    window.open(`/conversation/${conversationId}`, '_blank');
  };

  // Calculate position based on index
  const rightPosition = 20 + (position * 340); // 320px width + 20px gap

  // Safety check - don't render if chat is undefined
  if (!chat) {
    return null;
  }

  return (
    <div 
      className={`fixed bottom-0 rounded-t-lg shadow-2xl transition-all duration-300 appearance-${appearanceMode} ${
        minimized ? 'h-14' : 'h-96'
      } ${
        isSuppleMode 
          ? 'supple-card-glass supple-border-subtle backdrop-blur-xl' 
          : 'bg-white border border-gray-200'
      }`}
      style={{ right: `${rightPosition}px`, width: '320px', zIndex: 1000 + position }}
    >
      {/* Header */}
      <div className={`flex items-center justify-between p-3 cursor-pointer ${
        isSuppleMode ? 'supple-border-subtle border-b' : 'border-b border-gray-200'
      }`}
           onClick={() => setMinimized(!minimized)}>
        <div className="flex items-center gap-2">
          {chat?.user?.picture ? (
            <img
              src={chat.user.picture}
              alt={chat.user.name}
              className="w-8 h-8 rounded-full"
            />
          ) : (
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              isSuppleMode 
                ? 'supple-btn-gradient-blue supple-text-white' 
                : 'bg-gray-300 text-gray-700'
            }`}>
              {chat?.user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
            </div>
          )}
          <div>
            <p className={`font-medium text-sm ${
              isSuppleMode ? 'supple-text-primary' : ''
            }`}>{chat?.user?.name}</p>
            <p className={`text-xs ${
              isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
            }`}>{chat?.role}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMinimized(!minimized);
            }}
            className={`p-1 rounded transition-colors ${
              isSuppleMode 
                ? 'hover:supple-card-glass-hover supple-text-muted' 
                : 'hover:bg-gray-100'
            }`}
          >
            {minimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose(chat?.id);
            }}
            className={`p-1 rounded transition-colors ${
              isSuppleMode 
                ? 'hover:supple-card-glass-hover supple-text-muted' 
                : 'hover:bg-gray-100'
            }`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      {!minimized && (
        <>
          <div className="flex-1 overflow-y-auto p-3 space-y-4" style={{ height: 'calc(100% - 120px)' }}>
            {!chat?.messages || chat.messages.length === 0 ? (
              <p className={`text-center text-sm ${
                isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
              }`}>Start a conversation...</p>
            ) : (
              chat.messages.map((msg, index) => (
                <div key={index} className="flex gap-2">
                  {/* Profile Photo */}
                  <div className="flex-shrink-0">
                    {msg.sender === 'me' ? (
                      currentUser?.picture ? (
                        <img
                          src={currentUser.picture}
                          alt={currentUser.name || 'You'}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                          isSuppleMode 
                            ? 'supple-btn-gradient-blue supple-text-white' 
                            : 'bg-blue-600 text-white'
                        }`}>
                          {currentUser?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || currentUser?.email?.[0]?.toUpperCase() || 'U'}
                        </div>
                      )
                    ) : (
                      chat?.user?.picture ? (
                        <img
                          src={chat.user.picture}
                          alt={chat.user.name}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                          isSuppleMode 
                            ? 'supple-btn-gradient-purple supple-text-white' 
                            : 'bg-gray-600 text-white'
                        }`}>
                          {chat?.user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                        </div>
                      )
                    )}
                  </div>
                  
                  {/* Message Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium ${
                        isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                      }`}>
                        {msg.sender === 'me' ? 'You' : chat?.user?.name}
                      </span>
                      <span className={`text-xs ${
                        isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
                      }`}>
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    
                    <div className={`${
                      isSuppleMode ? 'supple-text-primary' : 'text-gray-800'
                    }`}>
                      <MessageRenderer 
                        content={msg.text}
                        onLoadChat={handleLoadSharedChat}
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className={`p-3 ${
            isSuppleMode ? 'supple-border-subtle border-t' : 'border-t border-gray-200'
          }`}>
            <div className="flex gap-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                className={`flex-1 px-3 py-2 rounded-lg focus:outline-none text-sm ${
                  isSuppleMode 
                    ? 'supple-card-glass supple-border-subtle focus:supple-border-gradient-blue supple-text-primary placeholder-supple-text-muted' 
                    : 'border border-gray-300 focus:ring-2 focus:ring-blue-500'
                }`}
              />
              <button
                onClick={handleSend}
                disabled={!message.trim()}
                className={`px-3 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isSuppleMode 
                    ? 'supple-btn-gradient-blue hover:supple-btn-gradient-blue-hover supple-shadow-sm' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatWindow;